import { useTranslation } from 'react-i18next';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ChartCard } from './ChartCard';
import { CATEGORICAL_COLORS, STATUS_COLORS, toStatusKey, useChartLocale } from './chartTheme';

interface PieChartProps {
  data: { name: string; value: number; color?: string }[];
  title?: string;
  height?: number;
  emptyMessage?: string;
  className?: string;
}

/**
 * Donut chart with an HTML legend (wraps cleanly on mobile and in RTL).
 * Names that are prescription statuses (e.g. "ACTIVE", "PARTIALLY FULFILLED") get the
 * StatusBadge color and a translated label automatically.
 */
export function PieChart({ data, title, height = 260, emptyMessage, className }: PieChartProps) {
  const { t } = useTranslation();
  const { locale, formatNumber, tooltip } = useChartLocale();

  const total = data.reduce((sum, d) => sum + (d.value || 0), 0);
  const isEmpty = data.length === 0 || total === 0;

  const chartData = data.map((d, i) => {
    const statusKey = toStatusKey(d.name);
    return {
      ...d,
      label: statusKey ? t(`prescription.status.${statusKey.toLowerCase()}`) : d.name,
      color:
        d.color ||
        (statusKey && STATUS_COLORS[statusKey]) ||
        CATEGORICAL_COLORS[i % CATEGORICAL_COLORS.length],
      percent: total > 0 ? (d.value / total) * 100 : 0,
    };
  });

  const formatPercent = (p: number) =>
    `${p.toLocaleString(locale, { maximumFractionDigits: p < 10 ? 1 : 0 })}%`;

  return (
    <ChartCard title={title} isEmpty={isEmpty} emptyMessage={emptyMessage} className={className}>
      <div className="relative" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPie>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius="62%"
              outerRadius="90%"
              paddingAngle={chartData.filter((d) => d.value > 0).length > 1 ? 2 : 0}
              stroke="none"
              isAnimationActive={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              {...tooltip}
              formatter={(value, name) => [
                `${formatNumber(value)} (${formatPercent(total > 0 ? (Number(value) / total) * 100 : 0)})`,
                name,
              ]}
            />
          </RechartsPie>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold tabular-nums text-gray-900">{formatNumber(total)}</span>
          <span className="text-xs text-gray-500">{t('charts.total')}</span>
        </div>
      </div>
      <ul className="mt-4 grid gap-x-4 gap-y-2 sm:grid-cols-2">
        {chartData.map((entry, index) => (
          <li key={`legend-${index}`} className="flex min-w-0 items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="min-w-0 flex-1 truncate text-gray-600" title={entry.label}>
              {entry.label}
            </span>
            <span className="shrink-0 font-medium tabular-nums text-gray-900">{formatNumber(entry.value)}</span>
            <span className="w-12 shrink-0 text-end text-xs tabular-nums text-gray-500">
              {formatPercent(entry.percent)}
            </span>
          </li>
        ))}
      </ul>
    </ChartCard>
  );
}
